import { useRef, useState } from 'react';
import { api, ApiError } from '../lib/api';
import { useSession } from '../lib/store';
import { Card, Notice } from './Shell';
import { TextField } from './Field';
import type { Branding } from '../lib/types';

const MAX_BYTES = 4 * 1024 * 1024;
const ACCEPT = 'image/jpeg,image/png,image/webp,image/svg+xml';
const HEX = /^#[0-9a-f]{6}$/i;

interface UploadTicket {
  fileKey: string;
  uploadUrl: string;
  requiredHeaders: Record<string, string>;
}

/**
 * The sitter's own look: two colours, a tagline, a logo and a hero photograph.
 *
 * Colours are stored, not computed: what the storefront does with them is the
 * storefront's business (see lib/branding.ts, which darkens a pale brand colour
 * rather than leaving a white label on a white button).
 */
export function BrandingCard({ tenantId, branding }: { tenantId: string; branding: Branding }) {
  const { tenant, load } = useSession();
  const [draft, setDraft] = useState<Branding>(branding);

  /**
   * PATCH returns the stored record, whose branding holds S3 keys and no signed
   * URLs, so the previews come from the session instead: it is refreshed after
   * every change and carries the hour-long URLs the storefront uses.
   */
  const live = tenant?.branding ?? branding;
  const [busy, setBusy] = useState<null | 'logo' | 'hero' | 'save'>(null);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const logoInput = useRef<HTMLInputElement>(null);
  const heroInput = useRef<HTMLInputElement>(null);

  const describe = (err: unknown) =>
    err instanceof ApiError ? err.message : 'Something went wrong. Try again.';

  /**
   * Two steps, the same as a pet document: the browser asks for a signed PUT
   * and sends the file straight to S3, so a multi-megabyte photograph never
   * passes through Lambda.
   */
  const upload = (kind: 'logo' | 'hero', file: File) => {
    if (file.size > MAX_BYTES) {
      setError('That image is over 4MB. Export it smaller and try again.');
      return;
    }
    setBusy(kind);
    setError(null);
    void (async () => {
      try {
        const ticket = await api<UploadTicket>(`/tenants/${tenantId}/branding/upload-url`, {
          method: 'POST',
          body: { kind, fileName: file.name, contentType: file.type, sizeBytes: file.size },
        });

        const put = await fetch(ticket.uploadUrl, {
          method: 'PUT',
          headers: ticket.requiredHeaders,
          body: file,
        });
        if (!put.ok) throw new Error(`Upload failed with ${put.status}`);

        const field = kind === 'logo' ? 'logoKey' : 'heroImageKey';
        const result = await api<{ tenant: { branding: Branding } }>(`/tenants/${tenantId}`, {
          method: 'PATCH',
          body: { branding: { [field]: ticket.fileKey } },
        });
        // The header and storefront read the signed URL off the session.
        setDraft((current) => ({ ...current, ...result.tenant.branding }));
        await load();
      } catch (err) {
        setError(describe(err));
      } finally {
        setBusy(null);
      }
    })();
  };

  const clearImage = (kind: 'logo' | 'hero') => {
    setBusy(kind);
    setError(null);
    void (async () => {
      try {
        const field = kind === 'logo' ? 'logoKey' : 'heroImageKey';
        const result = await api<{ tenant: { branding: Branding } }>(`/tenants/${tenantId}`, {
          method: 'PATCH',
          body: { branding: { [field]: null } },
        });
        setDraft((current) => ({ ...current, ...result.tenant.branding }));
        await load();
      } catch (err) {
        setError(describe(err));
      } finally {
        setBusy(null);
      }
    })();
  };

  const save = (event: React.FormEvent) => {
    event.preventDefault();
    if (!HEX.test(draft.primaryColor) || !HEX.test(draft.accentColor)) {
      setError('Colours must be a six-digit hex value, for example #5b4b8a.');
      return;
    }
    setBusy('save');
    setError(null);
    setSaved(false);
    void (async () => {
      try {
        const result = await api<{ tenant: { branding: Branding } }>(`/tenants/${tenantId}`, {
          method: 'PATCH',
          body: {
            branding: {
              primaryColor: draft.primaryColor,
              accentColor: draft.accentColor,
              tagline: draft.tagline || null,
            },
          },
        });
        setDraft((current) => ({ ...current, ...result.tenant.branding }));
        setSaved(true);
        await load();
      } catch (err) {
        setError(describe(err));
      } finally {
        setBusy(null);
      }
    })();
  };

  return (
    <Card tab="Your look" className="mb-6 p-5">
      {error && (
        <div className="mb-4">
          <Notice tone="error">{error}</Notice>
        </div>
      )}
      {saved && !error && (
        <div className="mb-4">
          <Notice tone="ok">Saved. Your booking page uses it now.</Notice>
        </div>
      )}

      <form onSubmit={save} className="space-y-6">
        <div className="grid gap-5 sm:grid-cols-2">
          <ColourField
            label="Brand colour"
            hint="Buttons, links and the highlight on your headline."
            value={draft.primaryColor}
            onChange={(primaryColor) => setDraft({ ...draft, primaryColor })}
          />
          <ColourField
            label="Accent colour"
            hint="Used sparingly, alongside the brand colour."
            value={draft.accentColor}
            onChange={(accentColor) => setDraft({ ...draft, accentColor })}
          />
        </div>

        <TextField
          label="Headline"
          value={draft.tagline ?? ''}
          onChange={(tagline) => setDraft({ ...draft, tagline })}
          hint="The first line on your booking page. Leave it empty for the default."
          placeholder="Your pet’s whole file, kept properly."
        />

        <div className="grid gap-5 sm:grid-cols-2">
          <ImageField
            label="Logo"
            hint="Shown in place of your name in the header. PNG, JPEG, WebP or SVG, up to 4MB."
            url={live.logoUrl}
            busy={busy === 'logo'}
            inputRef={logoInput}
            onPick={(file) => upload('logo', file)}
            onClear={() => clearImage('logo')}
            frame="h-16 w-full object-contain"
          />
          <ImageField
            label="Hero photograph"
            hint="Replaces the illustration beside your headline."
            url={live.heroImageUrl}
            busy={busy === 'hero'}
            inputRef={heroInput}
            onPick={(file) => upload('hero', file)}
            onClear={() => clearImage('hero')}
            frame="h-32 w-full object-cover"
          />
        </div>

        <button type="submit" className="btn btn-primary" disabled={busy !== null}>
          {busy === 'save' ? 'Saving' : 'Save look'}
        </button>
      </form>
    </Card>
  );
}

function ColourField({
  label,
  hint,
  value,
  onChange,
}: {
  label: string;
  hint: string;
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <div>
      <label className="field-label" htmlFor={`colour-${label}`}>
        {label}
      </label>
      <div className="mt-1 flex items-center gap-3">
        {/* Two ways in for the same value: a picker for choosing, and the hex
            for pasting one from a brand guide. */}
        <input
          id={`colour-${label}`}
          type="color"
          value={HEX.test(value) ? value : '#5b4b8a'}
          onChange={(event) => onChange(event.currentTarget.value)}
          className="h-10 w-14 cursor-pointer rounded border border-[color:var(--color-rule)] bg-transparent p-1"
        />
        <input
          type="text"
          value={value}
          onChange={(event) => onChange(event.currentTarget.value)}
          spellCheck={false}
          className="w-32 rounded border border-[color:var(--color-rule)] bg-[color:var(--color-sheet)] px-3 py-2 font-mono text-sm"
        />
      </div>
      <span className="mt-1 block text-xs text-[color:var(--color-ink-soft)]">{hint}</span>
    </div>
  );
}

function ImageField({
  label,
  hint,
  url,
  busy,
  inputRef,
  onPick,
  onClear,
  frame,
}: {
  label: string;
  hint: string;
  url?: string;
  busy: boolean;
  inputRef: React.RefObject<HTMLInputElement>;
  onPick: (file: File) => void;
  onClear: () => void;
  frame: string;
}) {
  return (
    <div>
      <p className="field-label">{label}</p>
      <div className="mt-1 rounded border border-[color:var(--color-rule)] p-3">
        {url ? (
          <img src={url} alt="" className={frame} />
        ) : (
          <p className={`${frame} flex items-center justify-center text-sm text-[color:var(--color-ink-faint)]`}>
            Nothing uploaded
          </p>
        )}
        <div className="mt-3 flex flex-wrap gap-2">
          <button
            type="button"
            className="btn btn-secondary"
            disabled={busy}
            onClick={() => inputRef.current?.click()}
          >
            {busy ? 'Uploading' : url ? 'Replace' : 'Upload'}
          </button>
          {url && (
            <button type="button" className="btn btn-quiet" disabled={busy} onClick={onClear}>
              Remove
            </button>
          )}
        </div>
        <input
          ref={inputRef}
          type="file"
          accept={ACCEPT}
          className="hidden"
          onChange={(event) => {
            const file = event.currentTarget.files?.[0];
            // Reset so picking the same file twice still fires a change.
            event.currentTarget.value = '';
            if (file) onPick(file);
          }}
        />
      </div>
      <span className="mt-1 block text-xs text-[color:var(--color-ink-soft)]">{hint}</span>
    </div>
  );
}
